import React, { useState } from 'react';

import { toast } from 'sonner';

import { Alert, Button, Modal } from '@shared/components/bootstrap';

import { Permission } from '@admin/components/helpers/permission';
import { ORDER_RETURN_SUBTYPE } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  OrderShowDocument,
  OrderStatusEnum,
  OrderSubtypeEnum,
  OrderTypeEnum,
  Status,
  useOrderReturnSubtypeReclassifyMutation,
} from '@admin/schema';
import { parseGQLErrorUnion } from '@admin/utils/parse_gql_error_union';

export const ReclassifyReturnSubtypeButton: React.FC<{
  orderID: string;
  status: OrderStatusEnum;
  subtype?: OrderSubtypeEnum | null;
  type: OrderTypeEnum;
}> = ({ orderID, status, subtype, type }) => {
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [reclassify, { loading }] = useOrderReturnSubtypeReclassifyMutation({
    client,
    refetchQueries: [{ query: OrderShowDocument, variables: { id: orderID } }],
  });

  if (type !== OrderTypeEnum.Return) {
    return null;
  }

  const canReclassify = status !== OrderStatusEnum.Canceled && status !== OrderStatusEnum.Completed;

  const onConfirm = async () => {
    setErrorMessage(undefined);
    const result = await reclassify({ variables: { orderID } });
    const [orderResult, mutationError] = parseGQLErrorUnion(result.data?.orderReturnSubtypeReclassify?.result);
    if (mutationError) {
      setErrorMessage(mutationError.errorMessage);
      return;
    }
    if (result.data?.orderReturnSubtypeReclassify?.status === Status.Ok) {
      const newSubtype = orderResult?.subtype;
      if (newSubtype && newSubtype === subtype) {
        toast.info(
          `Classification unchanged — this return is already ${subtypeLabel} based on current inventory rules.`,
        );
      } else {
        const label = newSubtype === OrderSubtypeEnum.Final ? 'Final' : 'Subsequent';
        toast.success(`Return subtype updated to ${label}. Pricing was refreshed when the subtype changed.`);
      }
      setOpen(false);
    }
  };

  const subtypeLabel = subtype === OrderSubtypeEnum.Final ? 'Final' : 'Subsequent';

  return (
    <Permission resource={ORDER_RETURN_SUBTYPE.resource} action={ORDER_RETURN_SUBTYPE.actions.update} denied={null}>
      {canReclassify && (
        <>
          {' '}
          <Button kind="default" onClick={() => setOpen(true)}>
            Re-classify subtype
          </Button>
          {open && (
            <Modal onClose={() => setOpen(false)}>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Re-classify return subtype</Modal.Title>
                  <Modal.Close close={() => setOpen(false)} />
                </Modal.Header>
                <Modal.Body>
                  {errorMessage && (
                    <Alert style="danger">
                      <strong>Error: </strong>
                      {errorMessage}
                    </Alert>
                  )}
                  <p>
                    This runs the same subtype classification used when booking or editing return inventory, then
                    updates pricing if the subtype changes. The order is currently classified as{' '}
                    <strong>{subtypeLabel}</strong>.
                  </p>
                </Modal.Body>
                <Modal.Footer>
                  <Button kind="default" onClick={() => setOpen(false)} disabled={loading}>
                    Cancel
                  </Button>
                  <Button kind="primary" onClick={onConfirm} disabled={loading}>
                    {loading ? 'Re-classifying…' : 'Re-classify'}
                  </Button>
                </Modal.Footer>
              </Modal.Content>
            </Modal>
          )}
        </>
      )}
    </Permission>
  );
};
